import { FormEvent, useEffect, useMemo, useState } from 'react';
import OneliveShell from './OneliveShell';
import OneliveAuthAlert from './OneliveAuthAlert';
import {
  checkNicknameDuplicate,
  checkRecommendCode,
  checkUsernameDuplicate,
  clearRegisterSession,
  getSavedRegisterCode,
  loginAccount,
  navigate,
  registerAccount,
  saveRegisterCode,
  showOneliveToast,
} from './api';
import {
  isMemberRegistrationEnabled,
  isRecommendRequired,
  joinFieldRules,
  loadOneliveEnvConfig,
  registerCompleteMessage,
  registerCompleteRedirect,
  requiresJoinAgreement,
  requiresNicknameDuplicateCheck,
  requiresPasswordConfirm,
  requiresUsernameDuplicateCheck,
  type OneliveEnvConfig,
} from './envConfig';

type Step = 'code' | 'form' | 'complete';
type Gender = 'M' | 'F' | '';
type AlertType = 'success' | 'error' | 'warning' | 'info';

type AuthAlert = {
  open: boolean;
  type: AlertType;
  title?: string;
  message: string;
};

const INITIAL_ALERT: AuthAlert = { open: false, type: 'info', message: '' };

export default function OneliveRegisterPage() {
  const [envConfig, setEnvConfig] = useState<OneliveEnvConfig | null>(null);
  const [configReady, setConfigReady] = useState(false);
  const [step, setStep] = useState<Step>('form');
  const [code, setCode] = useState('');
  const [codeChecked, setCodeChecked] = useState(false);
  const [checkingCode, setCheckingCode] = useState(false);
  const [username, setUsername] = useState('');
  const [usernameChecked, setUsernameChecked] = useState(false);
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [nickname, setNickname] = useState('');
  const [nicknameChecked, setNicknameChecked] = useState(false);
  const [checkingNickname, setCheckingNickname] = useState(false);
  const [realName, setRealName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [gender, setGender] = useState<Gender>('');
  const [mobile, setMobile] = useState('');
  const [agreeAll, setAgreeAll] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [alert, setAlert] = useState<AuthAlert>(INITIAL_ALERT);
  const [registeredAccount, setRegisteredAccount] = useState<{ username: string; password: string } | null>(null);

  const fields = useMemo(() => joinFieldRules(envConfig), [envConfig]);
  const recommendRequired = isRecommendRequired(envConfig);
  const memberEnabled = isMemberRegistrationEnabled(envConfig);
  const needTerms = requiresJoinAgreement(envConfig);
  const needPasswordConfirm = requiresPasswordConfirm(envConfig);
  const needUsernameCheck = requiresUsernameDuplicateCheck(envConfig);
  const needNicknameCheck = requiresNicknameDuplicateCheck(envConfig);
  const completeMessage = registerCompleteMessage(envConfig);
  const completeRedirect = registerCompleteRedirect(envConfig);

  const showAlert = (type: AlertType, message: string, title?: string) => {
    setAlert({ open: true, type, message, title });
  };

  const closeAlert = () => {
    setAlert((prev) => ({ ...prev, open: false }));
  };

  useEffect(() => {
    let active = true;

    const init = async () => {
      clearRegisterSession();
      const config = await loadOneliveEnvConfig();
      if (!active) return;

      setEnvConfig(config);
      setConfigReady(true);

      const params = new URLSearchParams(window.location.search);
      const preset = params.get('code') || params.get('recommend_code') || '';
      if (preset) {
        setCode(preset.toUpperCase());
      }

      if (isRecommendRequired(config)) {
        setStep('code');
        setUsernameChecked(!requiresUsernameDuplicateCheck(config));
        setNicknameChecked(!requiresNicknameDuplicateCheck(config));
      } else {
        setStep('form');
        setUsernameChecked(true);
        setNicknameChecked(true);
        if (preset) {
          setCodeChecked(true);
          saveRegisterCode(preset.toUpperCase());
        }
      }
    };

    void init();

    return () => {
      active = false;
    };
  }, []);

  const onCheckCode = async () => {
    const trimmed = code.trim().toUpperCase();
    if (trimmed.length < 4) {
      showAlert('warning', '정확한 추천코드를 입력해 주시기 바랍니다.');
      return;
    }
    setCheckingCode(true);
    try {
      const valid = await checkRecommendCode(trimmed);
      if (!valid) {
        setCodeChecked(false);
        showAlert('error', '유효하지 않은 추천코드입니다.\n다시 확인해 주세요.', '추천코드 확인');
        return;
      }
      setCode(trimmed);
      setCodeChecked(true);
      showAlert('success', '사용 가능한 추천코드입니다.', '추천코드 확인');
    } catch {
      setCodeChecked(false);
      showAlert('error', '추천코드 확인 중 오류가 발생했습니다.\n잠시 후 다시 시도해 주세요.', '추천코드 확인');
    } finally {
      setCheckingCode(false);
    }
  };

  const onConfirmCode = () => {
    if (!codeChecked) {
      showAlert('warning', '추천코드를 먼저 확인해 주세요.\n코드체크 버튼을 눌러 주세요.');
      return;
    }
    saveRegisterCode(code.trim().toUpperCase());
    setStep('form');
  };

  const onBackToCode = () => {
    if (!recommendRequired) return;
    clearRegisterSession();
    setStep('code');
    setCodeChecked(false);
  };

  const onCheckUsername = async () => {
    const trimmed = username.trim();
    if (trimmed.length < 4) {
      showOneliveToast('아이디는 4자 이상 입력해 주세요.', 'warning');
      return;
    }
    setCheckingUsername(true);
    try {
      const result = await checkUsernameDuplicate(trimmed);
      setUsernameChecked(result.available);
      showOneliveToast(result.message, result.available ? 'success' : 'error');
    } catch {
      setUsernameChecked(false);
      showOneliveToast('아이디 확인 중 오류가 발생했습니다.', 'error');
    } finally {
      setCheckingUsername(false);
    }
  };

  const onCheckNickname = async () => {
    const trimmed = nickname.trim();
    if (trimmed.length < 2) {
      showOneliveToast('닉네임은 2자 이상 입력해 주세요.', 'warning');
      return;
    }
    setCheckingNickname(true);
    try {
      const result = await checkNicknameDuplicate(trimmed);
      setNicknameChecked(result.available);
      showOneliveToast(result.message, result.available ? 'success' : 'error');
    } catch {
      setNicknameChecked(false);
      showOneliveToast('닉네임 확인 중 오류가 발생했습니다.', 'error');
    } finally {
      setCheckingNickname(false);
    }
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (needUsernameCheck && !usernameChecked) {
      showOneliveToast('아이디 중복확인을 해주세요.', 'warning');
      return;
    }
    if (fields.nickname.visible && needNicknameCheck && !nicknameChecked) {
      showOneliveToast('닉네임 중복확인을 해주세요.', 'warning');
      return;
    }
    if (fields.real_name.visible && fields.real_name.required && !realName.trim()) {
      showOneliveToast('이름을 입력해 주세요.', 'warning');
      return;
    }
    if (fields.gender.visible && fields.gender.required && !gender) {
      showOneliveToast('성별을 선택해 주세요.', 'warning');
      return;
    }
    if (fields.mobile.visible && fields.mobile.required && !mobile.trim()) {
      showOneliveToast('휴대폰 번호를 입력해 주세요.', 'warning');
      return;
    }
    if (fields.email.visible && fields.email.required && !email.trim()) {
      showOneliveToast('이메일을 입력해 주세요.', 'warning');
      return;
    }
    if (needTerms && !agreeAll) {
      showOneliveToast('이용약관에 동의해 주세요.', 'warning');
      return;
    }
    if (needPasswordConfirm && password !== passwordConfirm) {
      showOneliveToast('비밀번호가 일치하지 않습니다.', 'warning');
      return;
    }

    const recommendCode = getSavedRegisterCode() || code.trim();
    const payload: Record<string, unknown> = {
      name: username.trim(),
      password,
      agree_terms: needTerms ? true : undefined,
      agree_privacy: needTerms ? true : undefined,
    };

    if (needPasswordConfirm) {
      payload.password_confirmation = passwordConfirm;
    }
    if (fields.nickname.visible) payload.nickname = nickname.trim();
    if (fields.real_name.visible && realName.trim()) payload.real_name = realName.trim();
    if (fields.mobile.visible && mobile.trim()) payload.mobile = mobile.trim();
    if (fields.gender.visible && gender) payload.gender = gender;
    if (fields.email.visible && email.trim()) payload.email = email.trim();
    if (recommendRequired && recommendCode) payload.recommend_code = recommendCode;

    setSubmitting(true);
    try {
      await registerAccount(payload);
      setRegisteredAccount({ username: username.trim(), password });
      clearRegisterSession();
      setStep('complete');
    } catch (err: unknown) {
      const message = (err as { message?: string })?.message || '회원가입 처리 중 오류가 발생했습니다.';
      showOneliveToast(message, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const onCompleteRegister = async () => {
    if (completeRedirect) {
      navigate(completeRedirect);
      return;
    }

    if (!registeredAccount) {
      navigate('/login');
      return;
    }

    setSubmitting(true);
    try {
      await loginAccount(registeredAccount.username, registeredAccount.password);
      showOneliveToast('로그인 되었습니다.', 'success');
      navigate('/');
    } catch (err: unknown) {
      const message = (err as { message?: string })?.message || '자동 로그인에 실패했습니다. 로그인 페이지에서 다시 시도해 주세요.';
      showOneliveToast(message, 'error');
      navigate('/login');
    } finally {
      setSubmitting(false);
    }
  };

  const isCompactStep = step === 'code' || step === 'complete';

  if (!configReady) {
    return (
      <OneliveShell>
        <div className="onelive-page onelive-auth-ref-page is-compact">
          <main className="onelive-main">
            <div className="onelive-ref-sub-title"><span>회원가입</span></div>
            <div className="onelive-auth-ref-card">
              <p className="onelive-auth-ref-code-status">설정을 불러오는 중...</p>
            </div>
          </main>
        </div>
      </OneliveShell>
    );
  }

  if (!memberEnabled) {
    return (
      <OneliveShell>
        <div className="onelive-page onelive-auth-ref-page is-compact">
          <main className="onelive-main">
            <div className="onelive-ref-sub-title"><span>회원가입</span></div>
            <div className="onelive-auth-ref-card">
              <h2>회원가입</h2>
              <p className="onelive-auth-ref-code-status">현재 회원가입이 중지되어 있습니다.</p>
              <button type="button" className="onelive-auth-ref-submit" onClick={() => navigate('/login')}>
                로그인하기
              </button>
            </div>
          </main>
        </div>
      </OneliveShell>
    );
  }

  return (
    <OneliveShell>
      <div className={`onelive-page onelive-auth-ref-page${isCompactStep ? ' is-compact' : ''}`}>
        <main className="onelive-main">
          <div className="onelive-ref-sub-title"><span>회원가입</span></div>

          <div className="onelive-auth-ref-card">
            <h2>회원가입</h2>

            {step === 'code' ? (
              <div className="onelive-auth-ref-step">
                <div className="onelive-auth-ref-code-row">
                  <input
                    type="text"
                    placeholder="추천코드"
                    value={code}
                    onChange={(ev) => {
                      setCode(ev.target.value.toUpperCase());
                      setCodeChecked(false);
                    }}
                  />
                  <button type="button" className="onelive-auth-ref-code-btn" onClick={() => void onCheckCode()} disabled={checkingCode}>
                    {checkingCode ? '...' : '코드체크'}
                  </button>
                </div>
                {codeChecked ? (
                  <p className="onelive-form-success onelive-auth-ref-code-status is-ok">추천코드가 확인되었습니다.</p>
                ) : (
                  <p className="onelive-auth-ref-code-status">추천코드를 입력한 뒤 코드체크를 눌러 주세요.</p>
                )}
                <button type="button" className="onelive-auth-ref-submit" onClick={onConfirmCode}>
                  확인
                </button>
              </div>
            ) : step === 'form' ? (
              <form className="onelive-auth-ref-form" onSubmit={onSubmit}>
                {recommendRequired && code ? (
                  <div className="onelive-auth-ref-readonly">추천코드: <strong>{code}</strong></div>
                ) : null}

                <div className="onelive-auth-ref-field-row">
                  <input
                    type="text"
                    placeholder="아이디"
                    value={username}
                    onChange={(e) => {
                      setUsername(e.target.value.replace(/[^a-zA-Z0-9_]/g, ''));
                      setUsernameChecked(!needUsernameCheck);
                    }}
                    required
                    minLength={4}
                    maxLength={20}
                  />
                  {needUsernameCheck ? (
                    <button type="button" className="onelive-auth-ref-code-btn" onClick={() => void onCheckUsername()} disabled={checkingUsername}>
                      {checkingUsername ? '...' : '중복확인'}
                    </button>
                  ) : null}
                </div>
                {needUsernameCheck && usernameChecked ? <p className="onelive-form-success">사용 가능한 아이디입니다.</p> : null}

                {fields.nickname.visible ? (
                  <>
                    <div className="onelive-auth-ref-field-row">
                      <input
                        type="text"
                        placeholder="닉네임"
                        value={nickname}
                        onChange={(e) => {
                          setNickname(e.target.value);
                          setNicknameChecked(!needNicknameCheck);
                        }}
                        required={fields.nickname.required}
                        minLength={2}
                        maxLength={30}
                      />
                      {needNicknameCheck ? (
                        <button type="button" className="onelive-auth-ref-code-btn" onClick={() => void onCheckNickname()} disabled={checkingNickname}>
                          {checkingNickname ? '...' : '중복확인'}
                        </button>
                      ) : null}
                    </div>
                    {needNicknameCheck && nicknameChecked ? <p className="onelive-form-success">사용 가능한 닉네임입니다.</p> : null}
                  </>
                ) : null}

                {fields.real_name.visible ? (
                  <input
                    type="text"
                    placeholder="이름"
                    value={realName}
                    onChange={(e) => setRealName(e.target.value)}
                    required={fields.real_name.required}
                    minLength={2}
                    maxLength={50}
                  />
                ) : null}

                {fields.email.visible ? (
                  <input
                    type="email"
                    placeholder="이메일"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required={fields.email.required}
                  />
                ) : null}

                <input
                  type="password"
                  placeholder="비밀번호 (8자 이상)"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                />
                {needPasswordConfirm ? (
                  <input
                    type="password"
                    placeholder="비밀번호 확인"
                    value={passwordConfirm}
                    onChange={(e) => setPasswordConfirm(e.target.value)}
                    required
                    minLength={8}
                  />
                ) : null}

                {fields.gender.visible ? (
                  <div className="onelive-auth-ref-gender">
                    <span>성별{fields.gender.required ? '' : ' (선택)'}</span>
                    <label>
                      <input type="radio" name="gender" value="M" checked={gender === 'M'} onChange={() => setGender('M')} />
                      <span>남자</span>
                    </label>
                    <label>
                      <input type="radio" name="gender" value="F" checked={gender === 'F'} onChange={() => setGender('F')} />
                      <span>여자</span>
                    </label>
                  </div>
                ) : null}

                {fields.mobile.visible ? (
                  <input
                    type="tel"
                    placeholder="휴대폰 번호"
                    value={mobile}
                    onChange={(e) => setMobile(e.target.value.replace(/[^\d\-+]/g, ''))}
                    required={fields.mobile.required}
                  />
                ) : null}

                {needTerms ? (
                  <button
                    type="button"
                    className={`onelive-auth-ref-terms-all${agreeAll ? ' is-checked' : ''}`}
                    onClick={() => setAgreeAll((prev) => !prev)}
                  >
                    <span className="onelive-auth-ref-terms-box" aria-hidden="true">{agreeAll ? '✓' : ''}</span>
                    <span>이용약관 및 개인정보처리방침 전체 동의</span>
                  </button>
                ) : null}

                <button type="submit" className="onelive-auth-ref-submit" disabled={submitting}>
                  {submitting ? '처리 중...' : '가입하기'}
                </button>
                {recommendRequired ? (
                  <button type="button" className="onelive-auth-ref-back" onClick={onBackToCode}>
                    추천코드 다시 입력
                  </button>
                ) : null}
              </form>
            ) : (
              <div className="onelive-auth-ref-step onelive-auth-ref-complete">
                <div className="onelive-auth-ref-complete-icon" aria-hidden="true">✓</div>
                <h3>회원가입 완료</h3>
                <p>{completeMessage}</p>
                <p className="onelive-auth-ref-complete-sub">
                  {completeRedirect ? '확인을 누르면 안내 페이지로 이동합니다.' : '확인을 누르면 자동으로 로그인됩니다.'}
                </p>
                <button type="button" className="onelive-auth-ref-submit" onClick={() => void onCompleteRegister()} disabled={submitting}>
                  {submitting ? '처리 중...' : '확인'}
                </button>
              </div>
            )}

            <p className="onelive-auth-ref-footer">
              {step === 'complete' ? null : (
                <>
                  이미 회원이신가요?{' '}
                  <button type="button" onClick={() => navigate('/login')}>로그인하기</button>
                </>
              )}
            </p>
          </div>
        </main>
      </div>

      <OneliveAuthAlert
        open={alert.open}
        type={alert.type}
        title={alert.title}
        message={alert.message}
        onClose={closeAlert}
      />
    </OneliveShell>
  );
}
